import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Phone, Search, User, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { navigationItems, contactInfo } from '@/data/navigation';
import { cn } from '@/lib/utils';
import RequestSamplesModal from '@/components/modals/RequestSamplesModal';

import GlobalSearch from '@/components/shared/GlobalSearch';

/** Accordion-style mobile dropdown for navigation items with sections */
const MobileDropdownContent = ({ item, onNavigate }) => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (idx) => {
    setOpenSection(prev => prev === idx ? null : idx);
  };

  const hasSections = item.dropdown?.sections?.length > 1;

  return (
    <div className="pb-3 space-y-1" data-testid={`mobile-dropdown-${item.id}`}>
      {item.dropdown.sections.map((section, idx) => (
        <div key={section.title || `section-${idx}`}>
          {hasSections ? (
            /* Accordion parent heading */
            <>
              <button
                onClick={() => toggleSection(idx)}
                className="w-full flex items-center justify-between pl-4 pr-2 py-2.5 text-left"
                aria-expanded={openSection === idx}
                data-testid={`mobile-section-${item.id}-${idx}`}
              >
                <span className="text-[13px] font-semibold tracking-wide uppercase text-muted-foreground">
                  {section.title}
                </span>
                <ChevronRight
                  className={cn(
                    "w-4 h-4 text-muted-foreground/60 transition-transform duration-200",
                    openSection === idx && "rotate-90"
                  )}
                />
              </button>
              {/* Collapsible child links */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-200",
                  openSection === idx ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                <ul className="pl-6 pr-2 pb-2 space-y-0.5">
                  {section.links.map((link, linkIdx) => (
                    <li key={link.href || link.label}>
                      <Link
                        to={link.href}
                        onClick={onNavigate}
                        className="text-sm text-foreground/80 hover:text-primary block py-2 pl-2 rounded-md hover:bg-muted/50 transition-colors"
                        data-testid={`mobile-link-${item.id}-${idx}-${linkIdx}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            /* Single section — show links directly without accordion */
            <div className="pl-4">
              {section.title && (
                <p className="text-[13px] font-semibold tracking-wide uppercase text-muted-foreground py-2">
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.links.map((link) => (
                  <li key={link.href || link.label}>
                    <Link
                      to={link.href}
                      onClick={onNavigate}
                      className="text-sm text-foreground/80 hover:text-primary block py-2 pl-2 rounded-md hover:bg-muted/50 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Divider between sections */}
          {hasSections && idx < item.dropdown.sections.length - 1 && (
            <div className="mx-4 border-b border-border/40" />
          )}
        </div>
      ))}

      {/* Featured links */}
      {item.dropdown.featured && (
        <div className="mx-4 pt-2 mt-1 border-t border-border/60 space-y-1">
          {item.dropdown.featured.cta && (
            <Link
              to={item.dropdown.featured.cta.href}
              onClick={onNavigate}
              className="flex items-center gap-2 text-sm font-semibold text-primary py-2"
              data-testid="mobile-view-all-products-link"
            >
              {item.dropdown.featured.cta.label}
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
          {item.dropdown.featured.link && (
            <Link
              to={item.dropdown.featured.link}
              onClick={onNavigate}
              className="flex items-center gap-2 text-sm font-semibold text-primary py-2"
              data-testid="mobile-custom-solutions-link"
            >
              {item.dropdown.featured.title}
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export const Header = ({ onOpenROIModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleDropdownToggle = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Banner */}
      <div className="bg-secondary text-secondary-foreground py-2 px-4">
        <div className="container-portico flex items-center justify-center text-sm">
          <button
            onClick={onOpenROIModal}
            className="hover:underline font-medium flex items-center gap-2 cursor-pointer"
          >
            <span>📘</span>
            Download Your Free Retail Flooring ROI Playbook
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={cn(
          'bg-primary-bg text-primary-foreground transition-shadow duration-base',
          isScrolled && 'shadow-lg'
        )}
        ref={dropdownRef}
      >
        <div className="container-portico">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/Assets/logos/PS2024_logo_wte_text.svg" 
                alt="Portico Systems" 
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigationItems.map((item) => (
                <div key={item.id} className="relative">
                  {item.hasDropdown ? (
                    <button
                      onClick={() => handleDropdownToggle(item.id)}
                      onMouseEnter={() => setActiveDropdown(item.id)}
                      className={cn(
                        'px-4 py-2 text-sm font-medium flex items-center gap-1 rounded-md',
                        'hover:bg-primary-light transition-colors duration-fast',
                        activeDropdown === item.id && 'bg-primary-light'
                      )}
                      aria-expanded={activeDropdown === item.id}
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform duration-fast',
                          activeDropdown === item.id && 'rotate-180'
                        )}
                      />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="px-4 py-2 text-sm font-medium hover:bg-primary-light rounded-md transition-colors duration-fast"
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {item.hasDropdown && activeDropdown === item.id && item.id === 'shop' && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[880px] bg-card text-card-foreground rounded-lg shadow-xl border border-border animate-slide-down"
                      onMouseLeave={() => setActiveDropdown(null)}
                      data-testid="products-mega-dropdown"
                    >
                      {(() => {
                        const categories = item.dropdown.sections.find(s => s.title === 'Categories');
                        const zones = item.dropdown.sections.find(s => s.title === 'Product Zone Comparison');
                        const tools = item.dropdown.sections.find(s => s.title === 'Product Tools');
                        const catLinks = categories?.links || [];
                        const mid = Math.ceil(catLinks.length / 2);
                        const catCol1 = catLinks.slice(0, mid);
                        const catCol2 = catLinks.slice(mid);

                        return (
                          <>
                            <div className="grid grid-cols-[220px_1fr_1fr] gap-0">
                              {/* LEFT — Product Tools + Featured Card */}
                              <div className="bg-muted p-5 rounded-l-lg flex flex-col gap-5">
                                {tools && (
                                  <div>
                                    <h4 className="font-heading font-semibold text-xs text-muted-foreground mb-2.5 uppercase tracking-wider">
                                      {tools.title}
                                    </h4>
                                    <ul className="space-y-1.5">
                                      {tools.links.map((link) => (
                                        <li key={link.href || link.label}>
                                          <Link to={link.href} className="text-sm text-foreground hover:text-primary transition-colors duration-fast block py-0.5">
                                            {link.label}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                <div className="border-t border-border/50 pt-4">
                                  <Link to={item.dropdown.featured.link || item.dropdown.featured.cta.href} className="block">
                                    <img
                                      src={item.dropdown.featured.image}
                                      alt={item.dropdown.featured.title}
                                      className="w-full h-24 object-cover rounded-md mb-3 hover:opacity-90 transition-opacity"
                                      loading="lazy"
                                    />
                                  </Link>
                                  <Link
                                    to={item.dropdown.featured.link || item.dropdown.featured.cta.href}
                                    className="font-heading font-semibold text-sm mb-1.5 block hover:text-primary transition-colors duration-fast"
                                    data-testid="desktop-custom-solutions-title-link"
                                  >
                                    {item.dropdown.featured.title}
                                  </Link>
                                  <p className="text-xs text-muted-foreground">{item.dropdown.featured.description}</p>
                                </div>
                              </div>

                              {/* CENTER — Categories Col 1 */}
                              <div className="p-5">
                                <h4 className="font-heading font-semibold text-xs text-muted-foreground mb-3 uppercase tracking-wider">Categories</h4>
                                <ul className="space-y-1.5">
                                  {catCol1.map((link) => (
                                    <li key={link.href || link.label}>
                                      <Link to={link.href} className="text-sm text-foreground hover:text-primary transition-colors duration-fast block py-0.5">
                                        {link.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {/* RIGHT — Categories Col 2 */}
                              <div className="p-5 pl-0">
                                <h4 className="font-heading font-semibold text-xs text-muted-foreground mb-3 uppercase tracking-wider">&nbsp;</h4>
                                <ul className="space-y-1.5">
                                  {catCol2.map((link) => (
                                    <li key={link.href || link.label}>
                                      <Link to={link.href} className="text-sm text-foreground hover:text-primary transition-colors duration-fast block py-0.5">
                                        {link.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>

                            {/* View All Products — sits directly underneath Categories */}
                            <div className="border-t border-border px-5 py-3 grid grid-cols-[220px_1fr]">
                              <div />
                              <Link
                                to={item.dropdown.featured.cta.href}
                                className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="desktop-view-all-products-link"
                              >
                                {item.dropdown.featured.cta.label} →
                              </Link>
                            </div>

                            {/* BOTTOM — Product Zone Comparison */}
                            {zones && (
                              <div className="border-t border-border px-5 py-4">
                                <div className="flex items-center gap-6">
                                  <h4 className="font-heading font-semibold text-xs text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                                    {zones.title}
                                  </h4>
                                  <div className="flex gap-4">
                                    {zones.links.map((link) => (
                                      <Link key={link.href || link.label} to={link.href} className="text-sm text-foreground hover:text-primary transition-colors duration-fast">
                                        {link.label}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Generic Dropdown — non-Products menus */}
                  {item.hasDropdown && activeDropdown === item.id && item.id !== 'shop' && (
                    <div
                      className="absolute top-full left-0 mt-2 w-[600px] bg-card text-card-foreground rounded-lg shadow-xl border border-border animate-slide-down"
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <div className="grid grid-cols-3 gap-0">
                        {/* Featured Section - LEFT */}
                        <div className="bg-muted p-6 rounded-l-lg">
                          <Link 
                            to={item.dropdown.featured.link || item.dropdown.featured.cta.href}
                            className="block"
                          >
                            <img
                              src={item.dropdown.featured.image}
                              alt={item.dropdown.featured.title}
                              className="w-full h-24 object-cover rounded-md mb-4 hover:opacity-90 transition-opacity"
                              loading="lazy"
                            />
                          </Link>
                          <h4 className="font-heading font-semibold text-sm mb-2">
                            {item.dropdown.featured.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mb-4">
                            {item.dropdown.featured.description}
                          </p>
                          <Link
                            to={item.dropdown.featured.cta.href}
                            className="text-xs font-semibold text-primary hover:underline"
                          >
                            {item.dropdown.featured.cta.label} →
                          </Link>
                        </div>

                        {/* Link Sections - RIGHT */}
                        <div className="col-span-2 p-6 space-y-6">
                          {item.dropdown.sections.map((section) => (
                            <div key={section.title || `section-${section.links?.[0]?.href || ''}`}>
                              <h4 className="font-heading font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wider">
                                {section.title}
                              </h4>
                              <ul className="space-y-2">
                                {section.links.map((link) => (
                                  <li key={link.href || link.label}>
                                    <Link
                                      to={link.href}
                                      className="text-sm text-foreground hover:text-primary transition-colors duration-fast block py-1"
                                    >
                                      {link.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2" ref={searchRef}>
              {/* Search */}
              <div className="relative hidden md:block">
                <button 
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-primary-light transition-colors duration-fast"
                  data-testid="header-search-btn"
                >
                  <Search className="w-5 h-5" />
                </button>
                
                {/* Search Dropdown */}
                {isSearchOpen && (
                  <div className="absolute top-full right-0 mt-2 w-[420px] z-50">
                    <GlobalSearch />
                  </div>
                )}
              </div>

              {/* Sample Request Icon */}
              <button 
                onClick={() => setIsSampleModalOpen(true)}
                className="hidden md:flex items-center justify-center w-10 h-10 rounded-full hover:bg-primary-light transition-colors duration-fast"
                title="Request Samples"
                data-testid="header-sample-request-btn"
              >
                <img 
                  src="/Assets/icons/samples_icon_wte.svg" 
                  alt="Request Samples" 
                  className="w-5 h-5"
                />
              </button>

              {/* Account */}
              <Link 
                to="/account" 
                className="hidden md:flex items-center justify-center w-10 h-10 rounded-full hover:bg-primary-light transition-colors duration-fast"
                data-testid="header-account-btn"
              >
                <User className="w-5 h-5" />
              </Link>

              <Link to="/contact">
                <Button
                  variant="secondary"
                  className="hidden md:flex font-semibold"
                >
                  Get Quote
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-primary-light transition-colors duration-fast"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card text-card-foreground border-t border-border animate-slide-down max-h-[calc(100vh-70px)] overflow-y-auto">
            <div className="container-portico py-3">
              {navigationItems.map((item) => (
                <div key={item.id} className="border-b border-border/60 last:border-0">
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(item.id)}
                        className="w-full flex items-center justify-between py-3.5 text-left font-medium"
                        aria-expanded={activeDropdown === item.id}
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'w-5 h-5 transition-transform duration-200',
                            activeDropdown === item.id && 'rotate-180'
                          )}
                        />
                      </button>
                      {activeDropdown === item.id && (
                        <MobileDropdownContent
                          item={item}
                          onNavigate={() => setIsMobileMenuOpen(false)}
                        />
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-3.5 font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 pb-2 flex flex-col gap-3">
                <button
                  onClick={() => { setIsMobileMenuOpen(false); setIsSampleModalOpen(true); }}
                  className="flex items-center gap-2 text-sm font-medium text-foreground py-2"
                  data-testid="mobile-sample-request-btn"
                >
                  <Package className="w-4 h-4" />
                  Request Samples
                </button>
                <a
                  href={`tel:${contactInfo.generalInquiries.phone}`}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Phone className="w-4 h-4" />
                  {contactInfo.generalInquiries.phone}
                </a>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="default" className="w-full">
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Request Samples Modal */}
      <RequestSamplesModal 
        isOpen={isSampleModalOpen} 
        onClose={() => setIsSampleModalOpen(false)} 
      />
    </header>
  );
};

export default Header;
